import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Tabs, Tab, Chip, CircularProgress, Alert, TextField, MenuItem, Button, Switch, FormControlLabel, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableHead, TableBody, TableRow, TableCell, InputAdornment, Autocomplete, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { List as StepsIcon } from '@mui/icons-material';
import { API_ENDPOINTS, getEndpoint } from '../api/apiConstants';
import StepDetailTabs from './StepDetailTabs';
import { api } from '../api/fetchWrapper';
import { fetchSteps, fetchStepEnums, createStep, updateStep, fetchJobDetails } from '../api/features/jobDetail/jobDetailApi';
import { SAP_COLORS, SAP_TYPOGRAPHY } from '../theme/sapDesignSystem';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { BackToLink } from '../components/layout';
import StepFlowAdapterIcon, { getStepFlowAdapterColor } from '../components/StepFlowAdapterIcon';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

// GET response mapping: [0]stepId, [1]jobDetailId, [2]parentId, [3]successKey, [4]successValue, [5]failureJobDetailId,
// [6]stepType, [7]objectType, [8]path, [9]requestTemplate, [10]responseType, [11]result, [12]sequence, [13]subType,
// [14]type, [15]headerTemplate, [16]responseFormatter, [17]errorFormatter, [18]loggingEnabled, [19]failureStepId,
// [20]stepCode, [21]automaticRetry, [22]beforeAgentCall, [23]clientId, [24]requestFormatter, [25]?, [26]requestType,
// [27]autoRetryEnabled, [28]maxAutoRetryCount
interface StepModel {
	id: number;
	jobDetailId: number;
	parentId: number | null;
	successKey: string;
	successValue: string;
	failureJobDetailId: number | null;
	adapter: string;
	objectType: string | null;
	path: string;
	requestTemplate: string;
	responseType: string;
	result: string;
	sequence: number;
	subType: string;
	type: string;
	headerTemplate: string | null;
	responseFormatter: string | null;
	errorFormatter: string | null;
	loggingEnabled: boolean | null;
	failureStepId: number | null;
	stepCode: string | null;
	automaticRetry: boolean | null;
	beforeAgentCall: boolean | null;
	clientId: number | null;
	requestFormatter: string | null;
	requestType: string | null;
	autoRetryEnabled: boolean | null;
	maxAutoRetryCount: number | null;
	active?: boolean; // alias for loggingEnabled in UI
}

const parseStepRow = (row: any[]): StepModel => {
	return {
		id: Number(row?.[0] ?? 0),
		jobDetailId: Number(row?.[1] ?? 0),
		parentId: row?.[2] != null ? Number(row[2]) : null,
		successKey: String(row?.[3] ?? ''),
		successValue: String(row?.[4] ?? ''),
		failureJobDetailId: row?.[5] != null ? Number(row[5]) : null,
		adapter: String(row?.[6] ?? ''),
		objectType: row?.[7] != null ? String(row[7]) : null,
		path: String(row?.[8] ?? ''),
		requestTemplate: String(row?.[9] ?? ''),
		responseType: String(row?.[10] ?? ''),
		result: String(row?.[11] ?? ''),
		sequence: Number(row?.[12] ?? 0),
		subType: String(row?.[13] ?? ''),
		type: String(row?.[14] ?? ''),
		headerTemplate: row?.[15] ?? null,
		responseFormatter: row?.[16] ?? null,
		errorFormatter: row?.[17] ?? null,
		loggingEnabled: row?.[18] ?? null,
		failureStepId: row?.[19] != null ? Number(row[19]) : null,
		stepCode: row?.[20] != null ? String(row[20]) : null,
		automaticRetry: row?.[21] ?? null,
		beforeAgentCall: row?.[22] ?? null,
		clientId: row?.[23] != null ? Number(row[23]) : null,
		requestFormatter: row?.[24] ?? null,
		requestType: row?.[26] != null ? String(row[26]) : null,
		autoRetryEnabled: row?.[27] ?? null,
		maxAutoRetryCount: row?.[28] != null ? Number(row[28]) : null,
		active: row?.[18] === true,
	};
};

const adapterGradient = (adapter: string, orderedAdapters: string[]): string => {
	const palettes = [
		['#E3F2FD', '#BBDEFB'],
		['#E8F5E9', '#C8E6C9'],
		['#FFF3E0', '#FFE0B2'],
		['#F3E5F5', '#E1BEE7'],
		['#E0F7FA', '#B2EBF2'],
		['#FBE9E7', '#FFCCBC'],
		['#F9FBE7', '#F0F4C3'],
		['#EDE7F6', '#D1C4E9'],
		['#E1F5FE', '#B3E5FC'],
		['#FCE4EC', '#F8BBD0'],
	];
	const index = Math.max(0, orderedAdapters.indexOf(adapter));
	const palette = palettes[index % palettes.length];
	return `linear-gradient(135deg, ${palette[0]}, ${palette[1]})`;
};

// Helper function to convert number to words (step_one, step_two, etc.)
const numberToStepWord = (num: number): string => {
	const words = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
		'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty'];
	if (num >= 0 && num <= 20) {
		return `step_${words[num]}`;
	}
	return `step_${num}`;
};

interface StepDetailsProps {
	/** When false, hide Add Step and make step list read-only. Based on ALLOW_STEP_EDIT permission. */
	allowStepEdit?: boolean;
	/** When false, step edit sub-tabs (step detail, settings, request validator) are read-only. Based on STEP_EDIT permission. */
	allowStepDetailEdit?: boolean;
}

const StepDetails: React.FC<StepDetailsProps> = ({ allowStepEdit = true, allowStepDetailEdit = true }) => {
	const { id } = useParams<{ id: string }>();
	const { clientCode } = useClientCode();

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [steps, setSteps] = useState<StepModel[]>([]);
	const [enums, setEnums] = useState<string[][]>([]);
	const [jobDetails, setJobDetails] = useState<Array<any[]>>([]);
	const [selectedStepId, setSelectedStepId] = useState<number | null>(null);
	const [rightTab, setRightTab] = useState(0);
	const [viewMode, setViewMode] = useState<'flow' | 'table' | 'detail'>('flow');
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [showGrid, setShowGrid] = useState(true);

	// Table Mode state
	const [tableSortField, setTableSortField] = useState<'sequence' | 'path' | 'status'>('sequence');
	const [tableSortDirection, setTableSortDirection] = useState<'asc' | 'desc'>('asc');
	const [tableFilter, setTableFilter] = useState('');
	const [editingSequence, setEditingSequence] = useState<number | null>(null);
	const [tempSequence, setTempSequence] = useState<number>(0);

	// Empty initial values for Add Step dialog (no pre-filled combobox/input values)
	const getEmptyAddValues = useCallback((): {
		stepCode: string;
		adapter: string;
		type: string;
		subType: string;
		parentId: number | null;
		failureJobDetailId: number | null;
		path: string;
		objectType: string;
		responseType: string;
		requestType: string;
		sequence: number;
		result: string;
		successKey: string;
		successValue: string;
		loggingEnabled: boolean;
		beforeAgentCall: boolean;
		automaticRetry: boolean;
		maxAutoRetryCount: number | null;
		headerTemplate: string;
		requestTemplate: string;
		responseFormatterJs: string;
		requestFormatterJs: string;
		errorFormatterJs: string;
	} => ({
		stepCode: '',
		adapter: '',
		type: '',
		subType: '',
		parentId: null,
		failureJobDetailId: null,
		path: '',
		objectType: '',
		responseType: '',
		requestType: '',
		sequence: 1,
		result: '',
		successKey: '',
		successValue: '',
		loggingEnabled: false,
		beforeAgentCall: false,
		automaticRetry: false,
		maxAutoRetryCount: null,
		headerTemplate: '',
		requestTemplate: '',
		responseFormatterJs: '',
		requestFormatterJs: '',
		errorFormatterJs: '',
	}), []);

	// Add Step dialog state (all fields per doc: Step Code, Step Type, Failure Job Detail Id, Type, Parent Id, Sub Type, Path, Object Type, Response Type, Request Type, Sequence, Result, Success Key, Success Value, Logging Enabled, Before Agent Call, Automatic Retry, Header Template, Request Template, Response Formatter, Request Formatter, Error Formatter)
	const [addOpen, setAddOpen] = useState(false);
	const [addStep, setAddStep] = useState(0); // 0 = Basics, 1 = Success & Behavior, 2 = Templates
	const [addValues, setAddValues] = useState(() => getEmptyAddValues());

	// Step 1 validation (Basics) - Removed required validation for stepCode and path to allow null steps
	const step1Errors = useMemo(() => {
		const e: Record<string, string> = {};
		if (!addValues.adapter) e.adapter = 'Step type is required';
		if (!Number.isFinite(addValues.sequence) || addValues.sequence <= 0) e.sequence = 'Sequence must be > 0';
		return e;
	}, [addValues]);

	const isStep1Valid = Object.keys(step1Errors).length === 0;
	const isStep2Valid = true;
	const isStep3Valid = true;

	const adaptersList = useMemo(() => (Array.isArray(enums?.[2]) ? enums[2] : []), [enums]);
	const typeOptions = useMemo(() => (Array.isArray(enums?.[2]) ? enums[2] : []), [enums]); // Type uses same as Step Type (enums[2])
	const objectTypeOptions = useMemo(() => (Array.isArray(enums?.[0]) ? enums[0] : []), [enums]); // Object Type uses enums[0]
	const resultOptions = useMemo(() => (Array.isArray(enums?.[1]) ? enums[1] : []), [enums]);
	const responseTypeOptions = useMemo(() => (Array.isArray(enums?.[0]) ? enums[0] : []), [enums]);

	// layout refs for connectors (vertical single column)
	const canvasRef = useRef<HTMLDivElement | null>(null);
	const nodeRefs = useRef<Map<number, HTMLDivElement>>(new Map());
	const [connectorLines, setConnectorLines] = useState<Array<{ x1: number; y1: number; x2: number; y2: number; markerEnd?: boolean }>>([]);
	const orderedSteps = useMemo(() => [...steps].sort((a, b) => a.sequence - b.sequence), [steps]);

	// Responsive step flow: full screen = one row centered; smaller screens = wrap in sequence (1→2→3, then 4→5→6)
	const theme = useTheme();
	const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
	const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
	const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
	const stepsPerRow = isLgUp ? 999 : isMdUp ? 4 : isSmUp ? 3 : 3;
	const stepRows = useMemo(() => {
		if (orderedSteps.length === 0) return [];
		const rows: StepModel[][] = [];
		for (let i = 0; i < orderedSteps.length; i += stepsPerRow) {
			rows.push(orderedSteps.slice(i, i + stepsPerRow));
		}
		return rows;
	}, [orderedSteps, stepsPerRow]);

	useEffect(() => {
		const fetchAll = async () => {
			if (!id) return;
			try {
				setLoading(true);
				setError(null);
				const [enumsResp, stepsResp, jobDetailsResp] = await Promise.all([
					fetchStepEnums({ clientCode, firstResult: -1, maxResult: 0 }),
					fetchSteps({ 
						clientCode, 
						firstResult: -1, 
						maxResult: 0, 
						jobDetailId: Number(id) 
					}),
					fetchJobDetails({ clientCode, firstResult: -1, maxResult: 0 }),
				]);
				setEnums(enumsResp);
				const parsed = stepsResp.map(parseStepRow).sort((a, b) => a.sequence - b.sequence);
				setSteps(parsed);
				setJobDetails(jobDetailsResp);
				setSelectedStepId(null);
				setViewMode('flow');
			} catch (e: any) {
				setError(e?.message || 'Failed to load steps');
			} finally {
				setLoading(false);
			}
		};
		fetchAll();
	}, [clientCode, id]);

	// Recompute connectors: right-angled (square) lines — horizontal then vertical, no diagonals
	useEffect(() => {
		const compute = () => {
			const container = canvasRef.current;
			if (!container || orderedSteps.length < 2) {
				setConnectorLines([]);
				return;
			}
			const containerRect = (container as HTMLElement).getBoundingClientRect();
			const segments: Array<{ x1: number; y1: number; x2: number; y2: number; markerEnd?: boolean }> = [];
			for (let i = 0; i < orderedSteps.length - 1; i++) {
				const fromEl = nodeRefs.current.get(orderedSteps[i].id);
				const toEl = nodeRefs.current.get(orderedSteps[i + 1].id);
				if (!fromEl || !toEl) continue;
				const fromRect = fromEl.getBoundingClientRect();
				const toRect = toEl.getBoundingClientRect();
				const x1 = fromRect.right - containerRect.left;
				const y1 = fromRect.top + fromRect.height / 2 - containerRect.top;
				const x2 = toRect.left - containerRect.left;
				const y2 = toRect.top + toRect.height / 2 - containerRect.top;
				const sameRow = Math.abs(y1 - y2) < 2;
				const sameCol = Math.abs(x1 - x2) < 2;
				if (sameRow || sameCol) {
					segments.push({ x1, y1, x2, y2, markerEnd: true });
				} else {
					// L-shape: horizontal to (x2, y1), then vertical to (x2, y2)
					segments.push({ x1, y1, x2, y2: y1, markerEnd: false });
					segments.push({ x1: x2, y1, x2, y2, markerEnd: true });
				}
			}
			setConnectorLines(segments);
		};
		compute();
		const onResize = () => compute();
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, [orderedSteps]);

	const selectedStep = useMemo(() => steps.find(s => s.id === selectedStepId) || null, [steps, selectedStepId]);

	// Canvas control handlers (returns promise so caller can await refetch before closing modal)
	const handleRefresh = useCallback(() => {
		const fetchAll = async () => {
			if (!id) return;
			try {
				setLoading(true);
				setError(null);
				const [enumsResp, stepsResp] = await Promise.all([
					fetchStepEnums({ clientCode, firstResult: -1, maxResult: 0 }),
					fetchSteps({
						clientCode,
						firstResult: -1,
						maxResult: 0,
						jobDetailId: Number(id)
					}),
				]);
				setEnums(enumsResp);
				const parsed = stepsResp.map(parseStepRow).sort((a, b) => a.sequence - b.sequence);
				setSteps(parsed);
			} catch (e: any) {
				setError(e?.message || 'Failed to load steps');
			} finally {
				setLoading(false);
			}
		};
		return fetchAll();
	}, [clientCode, id]);

	const handleFitToScreen = useCallback(() => {
		// Simple implementation - could be enhanced with actual canvas fitting logic
		const container = canvasRef.current;
		if (container) {
			container.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}, []);

	const handleAddStep = useCallback(() => {
		setAddValues({ ...getEmptyAddValues(), sequence: Math.max(1, (steps?.length || 0) + 1) });
		setAddStep(0); // Reset to step 1
		setAddOpen(true);
	}, [steps, getEmptyAddValues]);

	const handleNextStep = useCallback(() => {
		setAddStep(prev => Math.min(2, prev + 1));
	}, []);

	const handlePrevStep = useCallback(() => {
		setAddStep(prev => Math.max(0, prev - 1));
	}, []);

	const handleCloseModal = useCallback(() => {
		setAddOpen(false);
		setAddStep(0);
		setAddValues(getEmptyAddValues());
	}, [getEmptyAddValues]);

	const handleSettings = useCallback(() => {
		// Placeholder for settings functionality
		logger.log('Settings clicked');
	}, []);

	const handleGridToggle = useCallback(() => {
		setShowGrid(prev => !prev);
	}, []);

	// Table sorting and filtering logic
	const sortedAndFilteredSteps = useMemo(() => {
		let filtered = steps.filter(step => {
			if (!tableFilter) return true;
			const searchTerm = tableFilter.toLowerCase();
			return (
				step.path.toLowerCase().includes(searchTerm) ||
				step.subType.toLowerCase().includes(searchTerm) ||
				step.adapter.toLowerCase().includes(searchTerm) ||
				(step.active !== false ? 'active' : 'disabled').includes(searchTerm)
			);
		});

		return filtered.sort((a, b) => {
			let aValue: any, bValue: any;
			
			switch (tableSortField) {
				case 'sequence':
					aValue = a.sequence;
					bValue = b.sequence;
					break;
				case 'path':
					aValue = a.path.toLowerCase();
					bValue = b.path.toLowerCase();
					break;
				case 'status':
					aValue = a.active !== false ? 'active' : 'disabled';
					bValue = b.active !== false ? 'active' : 'disabled';
					break;
				default:
					return 0;
			}

			if (aValue < bValue) return tableSortDirection === 'asc' ? -1 : 1;
			if (aValue > bValue) return tableSortDirection === 'asc' ? 1 : -1;
			return 0;
		});
	}, [steps, tableFilter, tableSortField, tableSortDirection]);

	const handleSort = useCallback((field: 'sequence' | 'path' | 'status') => {
		if (tableSortField === field) {
			setTableSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
		} else {
			setTableSortField(field);
			setTableSortDirection('asc');
		}
	}, [tableSortField]);

	const handleSequenceEdit = useCallback((stepId: number, currentSequence: number) => {
		setEditingSequence(stepId);
		setTempSequence(currentSequence);
	}, []);

	const handleSequenceSave = useCallback(async (stepId: number) => {
		try {
			const step = steps.find(s => s.id === stepId);
			if (!step) return;

			// Check for sequence conflicts
			const conflictStep = steps.find(s => s.id !== stepId && s.sequence === tempSequence);
			if (conflictStep) {
				setError(`Sequence ${tempSequence} is already used by step ${conflictStep.path}`);
				return;
			}

			// Update the step
			const updatedStep = { ...step, sequence: tempSequence };
			await updateStep({ 
				clientCode, 
				stepData: {
					id: updatedStep.id,
					jobDetailId: updatedStep.jobDetailId,
					successKey: updatedStep.successKey || '${Result?string}',
					successValue: updatedStep.successValue || 'true',
					subType: updatedStep.subType || 'POST|application/json',
					path: updatedStep.path || '',
					requestTemplate: updatedStep.requestTemplate || '',
					responseType: updatedStep.responseType || 'JSON',
					sequence: updatedStep.sequence,
					type: updatedStep.type || updatedStep.adapter || 'REST',
					stepType: updatedStep.adapter || 'REST',
					result: updatedStep.result || 'OBJECT',
					objectType: updatedStep.objectType ?? null,
					headerTemplate: updatedStep.headerTemplate || '',
					responseFormatterJs: updatedStep.responseFormatter || '',
					requestFormatterJs: updatedStep.requestFormatter ?? null,
					errorFormatterJs: updatedStep.errorFormatter || '',
					clientId: updatedStep.clientId || 0,
					loggingEnabled: updatedStep.loggingEnabled ?? updatedStep.active ?? true,
					requestType: updatedStep.requestType || 'STRING',
					stepCode: updatedStep.stepCode ?? null,
					parentId: updatedStep.parentId ?? null,
					failureJobDetailId: updatedStep.failureJobDetailId ?? null,
					failureStepId: updatedStep.failureStepId ?? null,
					beforeAgentCall: updatedStep.beforeAgentCall ?? false,
					autoRetryEnabled: updatedStep.autoRetryEnabled ?? null,
					maxAutoRetryCount: updatedStep.maxAutoRetryCount ?? null,
				}
			});

			setSteps(prev => prev.map(s => s.id === stepId ? updatedStep : s));
			setEditingSequence(null);
			setError(null);
		} catch (error) {
			logger.error('Error updating sequence:', error);
			setError('Failed to update sequence');
		}
	}, [steps, clientCode, tempSequence]);

	const handleSequenceCancel = useCallback(() => {
		setEditingSequence(null);
		setTempSequence(0);
	}, []);

	// Circular icon-only step node; category color for border/icon so users can scan by type. Responsive size.
	const StepNode = useCallback(({ step, isSelected, onClick }: { step: StepModel; isSelected: boolean; onClick: () => void }) => {
		const adapterName = step.adapter || 'STEP';
		const categoryColor = getStepFlowAdapterColor(adapterName);
		return (
			<Tooltip title={adapterName} placement="top" arrow>
				<Box
					ref={(el: HTMLDivElement) => {
						if (el) nodeRefs.current.set(step.id, el); else nodeRefs.current.delete(step.id);
					}}
					onClick={onClick}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							onClick();
						}
					}}
					role="button"
					aria-label={`Step: ${adapterName}`}
					aria-selected={isSelected}
					tabIndex={0}
					sx={{
						width: { xs: 48, sm: 56, md: 72 },
						height: { xs: 48, sm: 56, md: 72 },
						minWidth: { xs: 48, sm: 56, md: 72 },
						minHeight: { xs: 48, sm: 56, md: 72 },
						borderRadius: '50%',
						backgroundColor: SAP_COLORS.sapWhite,
						border: isSelected ? `3px solid ${categoryColor}` : `2px solid ${categoryColor}`,
						boxShadow: isSelected ? `0 4px 20px ${categoryColor}40` : `0 2px 8px rgba(0,0,0,0.08)`,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						flexShrink: 0,
						cursor: 'pointer',
						transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
						color: categoryColor,
						'&:hover': {
							borderColor: categoryColor,
							boxShadow: `0 4px 20px ${categoryColor}50`,
							transform: 'scale(1.05)',
						},
						'&:focus-visible': {
							outline: `3px solid ${categoryColor}`,
							outlineOffset: '3px',
						},
					}}
				>
					<StepFlowAdapterIcon
						adapter={adapterName}
						sx={{ fontSize: { xs: 24, sm: 28, md: 36 } }}
						useCategoryColor
					/>
				</Box>
			</Tooltip>
		);
	}, []);

	const [editValues, setEditValues] = useState<Partial<StepModel>>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});
    const [isDirty, setIsDirty] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
	useEffect(() => {
		if (selectedStep) {
			// Extend selectedStep with a few UI-only fields; cast to keep TypeScript happy
			setEditValues({
				...selectedStep,
				responseFormatterJs: (selectedStep as any).responseFormatterJs || selectedStep.responseFormatter || '',
				requestFormatterJs: (selectedStep as any).requestFormatterJs || selectedStep.requestFormatter || null,
				errorFormatterJs: (selectedStep as any).errorFormatterJs || selectedStep.errorFormatter || '',
				autoRetryEnabled: (selectedStep as any).autoRetryEnabled ?? (selectedStep as any).automaticRetry ?? null,
			} as any);
			setTouched({});
			setIsDirty(false);
		}
	}, [selectedStepId]);

	const updateEditField = (key: keyof StepModel, value: any) => {
		setEditValues(prev => ({ ...prev, [key]: value }));
		setTouched(prev => ({ ...prev, [String(key)]: true }));
		setIsDirty(true);
	};

	// Basic validation aligned with spec - Removed required validation for stepCode, subType, and path to allow null steps
	const validationErrors = useMemo(() => {
		const errs: Record<string, string> = {};
		const v = editValues;
		if (!v?.adapter || String(v.adapter).trim() === '') errs.adapter = 'Step Type is required';
		if (v?.sequence === undefined || v?.sequence === null || Number.isNaN(Number(v.sequence))) errs.sequence = 'Sequence is required';
		return errs;
	}, [editValues]);

    const saveSelectedStep = async () => {
		const idToUpdate = selectedStep?.id;
		if (!idToUpdate) return;
		if (Object.keys(validationErrors).length > 0) return; // guard
		
		try {
			const stepData = {
				id: idToUpdate,
				jobDetailId: selectedStep?.jobDetailId || 0,
				successKey: editValues.successKey ?? '${Result?string}',
				successValue: editValues.successValue ?? 'true',
				subType: editValues.subType?.trim() || '',
				path: editValues.path?.trim() || '',
				requestTemplate: editValues.requestTemplate ?? '',
				responseType: editValues.responseType || 'JSON',
				sequence: editValues.sequence ?? 1,
				type: editValues.type || editValues.adapter || 'REST',
				stepType: editValues.adapter ?? 'REST',
				result: editValues.result || 'OBJECT',
				objectType: editValues.objectType?.trim() || null,
				headerTemplate: editValues.headerTemplate ?? '',
				responseFormatterJs: (editValues as any).responseFormatterJs ?? (editValues as any).responseFormatter ?? editValues.responseFormatter ?? '',
				requestFormatterJs: (editValues as any).requestFormatterJs ?? (editValues as any).requestFormatter ?? null,
				errorFormatterJs: (editValues as any).errorFormatterJs ?? (editValues as any).errorFormatter ?? editValues.errorFormatter ?? '',
				clientId: editValues.clientId ?? 0,
				loggingEnabled: (editValues as any).loggingEnabled ?? editValues.active ?? true,
				requestType: editValues.requestType || 'STRING',
				stepCode: (editValues as any).stepCode?.trim() || null,
				parentId: (editValues as any).parentId ?? null,
				failureJobDetailId: (editValues as any).failureJobDetailId ?? null,
				failureStepId: (editValues as any).failureStepId ?? null,
				beforeAgentCall: (editValues as any).beforeAgentCall ?? false,
				autoRetryEnabled: (editValues as any).autoRetryEnabled ?? null,
				maxAutoRetryCount: (editValues as any).maxAutoRetryCount ?? null,
			};
			
			await updateStep({ clientCode, stepData });
			setSteps(prev => prev.map(s => (s.id === idToUpdate ? { ...s, ...(editValues as StepModel) } : s)));
			setIsDirty(false);
			setLastSavedAt(new Date().toLocaleTimeString());
			toast.success('Step saved successfully');
		} catch (error: any) {
			logger.error('Error saving step:', error);
			const errorMessage = error?.message || 'Failed to save step';
			setError(errorMessage);
			toast.error(errorMessage);
		}
	};

	const revertChanges = () => {
		if (selectedStep) {
			setEditValues({ ...selectedStep });
			setTouched({});
			setIsDirty(false);
		}
	};

	// Enhanced keyboard shortcuts for better UX
	const firstFieldRef = useRef<HTMLInputElement | null>(null);
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			// Save shortcut (Ctrl/Cmd+S)
			const isSaveCombo = (e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S');
			if (isSaveCombo) {
				e.preventDefault();
				if (viewMode === 'detail' && selectedStepId) {
					saveSelectedStep();
				}
				return;
			}
			
			// Escape to go back to flow view
			if (e.key === 'Escape' && viewMode === 'detail') {
				setViewMode('flow');
				return;
			}
			
			// Enter to focus first field when in detail mode
			if (e.key === 'Enter' && viewMode === 'detail') {
				firstFieldRef.current?.focus();
				return;
			}
			
			// Arrow keys for step navigation in flow mode
			if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && viewMode === 'flow') {
				if (!steps.length) return;
				const ordered = [...steps].sort((a, b) => a.sequence - b.sequence);
				const currentIdx = selectedStepId ? ordered.findIndex(s => s.id === selectedStepId) : -1;
				
				if (e.key === 'ArrowUp' && currentIdx > 0) {
					setSelectedStepId(ordered[currentIdx - 1].id);
				} else if (e.key === 'ArrowDown' && currentIdx < ordered.length - 1) {
					setSelectedStepId(ordered[currentIdx + 1].id);
				} else if (currentIdx === -1 && ordered.length > 0) {
					// If no step selected, select first one
					setSelectedStepId(ordered[0].id);
				}
				return;
			}
			
			// Space or Enter to open detail view for selected step
			if ((e.key === ' ' || e.key === 'Enter') && viewMode === 'flow' && selectedStepId) {
				e.preventDefault();
				setViewMode('detail');
				return;
			}
			
			// Tab navigation improvements
			if (e.key === 'Tab' && viewMode === 'detail') {
				// Allow normal tab behavior but ensure focus stays within the form
				return;
			}
		};
		window.addEventListener('keydown', handler);
		return () => window.removeEventListener('keydown', handler);
	}, [steps, selectedStepId, saveSelectedStep, viewMode]);

	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error">{error}</Alert>
		);
	}

	// Flow-only view: show only the flow canvas and nodes
	if (viewMode === 'flow') {
		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
				<Card sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
					<CardContent sx={{ p: 0 }}>
						{/* Two-Step Add Step Dialog */}
						<Dialog open={addOpen} onClose={handleCloseModal} fullWidth maxWidth="md">
							<DialogTitle>
								<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
									<Typography variant="h6">Add New Step</Typography>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
										{[{ i: 0, label: 'Basics' }, { i: 1, label: 'Success &amp; Behavior' }, { i: 2, label: 'Templates' }].map(({ i, label }) => (
											<Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12, color: addStep === i ? SAP_COLORS.sapBlue : 'text.secondary' }}>
												<Box sx={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: addStep === i ? SAP_COLORS.sapBlue : SAP_COLORS.sapGray3, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold }}>{i + 1}</Box>
												<Typography component="span" sx={{ fontSize: 12 }}>{label === 'Success &amp; Behavior' ? 'Success & Behavior' : label}</Typography>
												{i < 2 && <Box sx={{ width: 12, height: 1, backgroundColor: SAP_COLORS.sapGray3, ml: 0.5 }} />}
											</Box>
										))}
									</Box>
								</Box>
							</DialogTitle>
							<DialogContent dividers>
								{addStep === 0 && (
									<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
										<TextField 
											label="Step Code" 
											value={addValues.stepCode} 
											onChange={(e) => setAddValues(v => ({ ...v, stepCode: e.target.value }))} 
											size="small" 
											fullWidth 
											placeholder="e.g. chec_all_step (optional)" 
										/>
										<Autocomplete
											options={adaptersList}
											value={addValues.adapter || null}
											onChange={(_, newValue) => setAddValues(v => ({ ...v, adapter: (typeof newValue === 'string' ? newValue : newValue ?? '') || '' }))}
											freeSolo
											renderInput={(params) => (
												<TextField
													{...params}
													label="Step Type"
													required
													helperText={step1Errors.adapter || ' '}
													error={!!step1Errors.adapter}
													size="small"
												/>
											)}
											fullWidth
										/>
										<Autocomplete
											options={jobDetails.map((job) => ({
												id: job[0],
												label: job[2] || `Job ${job[0]}`,
											}))}
											value={jobDetails.find((job) => job[0] === addValues.failureJobDetailId) ? {
												id: addValues.failureJobDetailId!,
												label: jobDetails.find((job) => job[0] === addValues.failureJobDetailId)?.[2] || `Job ${addValues.failureJobDetailId}`,
											} : null}
											onChange={(_, newValue) => {
												setAddValues(v => ({ ...v, failureJobDetailId: newValue?.id || null }));
											}}
											getOptionLabel={(option) => option.label}
											isOptionEqualToValue={(option, value) => option.id === value.id}
											size="small"
											renderInput={(params) => (
												<TextField
													{...params}
													label="Failure Job Detail Id"
													placeholder="Search job..."
												/>
											)}
											fullWidth
										/>
										<Autocomplete
											options={typeOptions}
											value={addValues.type || null}
											onChange={(_, newValue) => setAddValues(v => ({ ...v, type: (typeof newValue === 'string' ? newValue : newValue ?? '') || '' }))}
											freeSolo
											renderInput={(params) => (
												<TextField {...params} label="Type" size="small" />
											)}
											fullWidth
										/>
										<TextField 
											label="Parent Id" 
											select
											value={addValues.parentId ?? ''} 
											onChange={(e) => setAddValues(v => ({ ...v, parentId: e.target.value === '' ? null : Number(e.target.value) }))} 
											size="small" 
											fullWidth 
										>
											<MenuItem value="">None</MenuItem>
											{steps.map((step) => {
												const displayLabel = step.stepCode || numberToStepWord(step.sequence);
												return <MenuItem key={step.id} value={step.id}>{displayLabel}</MenuItem>;
											})}
										</TextField>
										<TextField 
											label="Sub Type" 
											value={addValues.subType} 
											onChange={(e) => setAddValues(v => ({ ...v, subType: e.target.value }))} 
											size="small" 
											fullWidth 
											placeholder="e.g. SFTP (optional)" 
										/>
										<TextField 
											label="Path" 
											value={addValues.path} 
											onChange={(e) => setAddValues(v => ({ ...v, path: e.target.value }))} 
											size="small" 
											fullWidth 
											placeholder="e.g. /api/endpoint (optional)" 
										/>
										<Autocomplete
											options={objectTypeOptions}
											value={addValues.objectType || null}
											onChange={(_, newValue) => setAddValues(v => ({ ...v, objectType: (typeof newValue === 'string' ? newValue : newValue ?? '') || '' }))}
											freeSolo
											renderInput={(params) => (
												<TextField {...params} label="Object Type" size="small" />
											)}
											fullWidth
										/>
										<TextField 
											label="Response Type" 
											value={addValues.responseType} 
											onChange={(e) => setAddValues(v => ({ ...v, responseType: e.target.value }))} 
											size="small" 
											fullWidth 
											placeholder="e.g. JSON, XML (optional)"
										/>
										<TextField 
											label="Request Type" 
											value={addValues.requestType} 
											onChange={(e) => setAddValues(v => ({ ...v, requestType: e.target.value }))} 
											size="small" 
											fullWidth 
										/>
										<TextField 
											label="Sequence" 
											type="number" 
											value={addValues.sequence} 
											onChange={(e) => setAddValues(v => ({ ...v, sequence: Number(e.target.value) }))} 
											size="small" 
											fullWidth 
											required 
											helperText={step1Errors.sequence || ' '} 
											error={!!step1Errors.sequence} 
											inputProps={{ min: 1 }} 
										/>
										<Autocomplete
											options={resultOptions}
											value={addValues.result || null}
											onChange={(_, newValue) => setAddValues(v => ({ ...v, result: (typeof newValue === 'string' ? newValue : newValue ?? '') || '' }))}
											freeSolo
											renderInput={(params) => (
												<TextField {...params} label="Result" size="small" />
											)}
											fullWidth
										/>
									</Box>
								)}
								{addStep === 1 && (
									<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
										<TextField label="Success Key" value={addValues.successKey} onChange={(e) => setAddValues(v => ({ ...v, successKey: e.target.value }))} size="small" fullWidth />
										<TextField label="Success Value" value={addValues.successValue} onChange={(e) => setAddValues(v => ({ ...v, successValue: e.target.value }))} size="small" fullWidth />
										<FormControlLabel control={<Switch checked={addValues.loggingEnabled} onChange={(e) => setAddValues(v => ({ ...v, loggingEnabled: e.target.checked }))} />} label="Logging Enabled" />
										<FormControlLabel control={<Switch checked={addValues.beforeAgentCall} onChange={(e) => setAddValues(v => ({ ...v, beforeAgentCall: e.target.checked }))} />} label="Before Agent Call" />
										<FormControlLabel control={<Switch checked={addValues.automaticRetry} onChange={(e) => setAddValues(v => ({ ...v, automaticRetry: e.target.checked }))} />} label="Automatic Retry" />
										<TextField label="Max Auto Retry Count" type="number" value={addValues.maxAutoRetryCount ?? ''} onChange={(e) => setAddValues(v => ({ ...v, maxAutoRetryCount: e.target.value === '' ? null : Number(e.target.value) }))} size="small" fullWidth inputProps={{ min: 0 }} />
									</Box>
								)}
								{addStep === 2 && (
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
										<TextField label="Header Template" value={addValues.headerTemplate} onChange={(e) => setAddValues(v => ({ ...v, headerTemplate: e.target.value }))} multiline minRows={3} size="small" fullWidth InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }} />
										<TextField label="Request Template" value={addValues.requestTemplate} onChange={(e) => setAddValues(v => ({ ...v, requestTemplate: e.target.value }))} multiline minRows={4} size="small" fullWidth InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }} />
										<TextField label="Response Formatter (JS)" value={addValues.responseFormatterJs} onChange={(e) => setAddValues(v => ({ ...v, responseFormatterJs: e.target.value }))} placeholder="function convertor(holder, myCode) { return 'Response formatter'; }" multiline minRows={3} size="small" fullWidth InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }} />
										<TextField label="Request Formatter (JS)" value={addValues.requestFormatterJs} onChange={(e) => setAddValues(v => ({ ...v, requestFormatterJs: e.target.value }))} placeholder="function convertor(holder, myCode) { return 'Request formatter'; }" multiline minRows={3} size="small" fullWidth InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }} />
										<TextField label="Error Formatter (JS)" value={addValues.errorFormatterJs} onChange={(e) => setAddValues(v => ({ ...v, errorFormatterJs: e.target.value }))} placeholder="function convertor(holder, myCode) { return 'Error formatter'; }" multiline minRows={3} size="small" fullWidth InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }} />
									</Box>
								)}
							</DialogContent>
							<DialogActions>
								<Button onClick={handleCloseModal}>Cancel</Button>
								{addStep < 2 ? (
									<>
										{addStep > 0 && <Button onClick={handlePrevStep}>Back</Button>}
										<Button variant="contained" disabled={addStep === 0 && !isStep1Valid} onClick={handleNextStep}>Next</Button>
									</>
								) : (
									<>
										<Button onClick={handlePrevStep}>Back</Button>
										<Button
											variant="contained"
											onClick={async () => {
												if (!id) return;
												try {
													const clientId = Number((steps?.[0]?.clientId ?? 0) || 0);
													const stepData = {
														stepCode: addValues.stepCode?.trim() || null,
														parentId: addValues.parentId,
														failureJobDetailId: addValues.failureJobDetailId,
														requestFormatterJs: addValues.requestFormatterJs?.trim() || null,
														beforeAgentCall: addValues.beforeAgentCall,
														autoRetryEnabled: addValues.automaticRetry,
														maxAutoRetryCount: addValues.maxAutoRetryCount,
														successKey: addValues.successKey,
														successValue: addValues.successValue,
														subType: addValues.subType?.trim() || '',
														path: addValues.path?.trim() || '',
														requestTemplate: addValues.requestTemplate,
														responseType: addValues.responseType || 'JSON',
														sequence: addValues.sequence,
														type: addValues.type || addValues.adapter || 'REST',
														stepType: addValues.adapter,
														result: addValues.result || 'OBJECT',
														objectType: addValues.objectType?.trim() || null,
														headerTemplate: addValues.headerTemplate,
														responseFormatterJs: addValues.responseFormatterJs || '',
														errorFormatterJs: addValues.errorFormatterJs || '',
														clientId,
														loggingEnabled: addValues.loggingEnabled,
														requestType: addValues.requestType || 'STRING',
														jobDetailId: Number(id),
													};
													const resp = await createStep({ clientCode, stepData });
													const newId = (resp as any)?.object?.id;
													await handleRefresh();
													if (newId) setSelectedStepId(newId);
													handleCloseModal();
													toast.success('Step created');
												} catch (e) {
													logger.error(e);
													setError('Failed to create step');
													toast.error('Failed to create step');
												}
											}}
										>
											Create
										</Button>
									</>
								)}
							</DialogActions>
						</Dialog>
						{/* Header: step count + Add Step */}
						<Box sx={{ 
							display: 'flex', 
							justifyContent: 'space-between', 
							alignItems: 'center', 
							p: 2, 
							borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
							backgroundColor: SAP_COLORS.sapWhite
						}}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<Typography sx={{ fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold, fontSize: 16, color: 'text.primary' }}>
									{steps.length === 0 ? '0 steps' : `${steps.length} ${steps.length === 1 ? 'step' : 'steps'}`}
								</Typography>
								<Tooltip title="Refresh">
									<IconButton size="small" onClick={handleRefresh} aria-label="Refresh steps" sx={{ color: 'text.secondary', '&:hover': { backgroundColor: SAP_COLORS.sapGray2, color: SAP_COLORS.sapBlue } }}>
										<RefreshIcon />
									</IconButton>
								</Tooltip>
							</Box>
							{allowStepEdit && (
								<Button
									variant="contained"
									startIcon={<AddIcon />}
									onClick={handleAddStep}
									sx={{
										backgroundColor: SAP_COLORS.sapBlue,
										color: 'white',
										fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
										textTransform: 'none',
										px: 2,
										py: 1,
										'&:hover': { backgroundColor: SAP_COLORS.sapBlueDark || '#1976d2', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
									}}
								>
									Add Step
								</Button>
							)}
						</Box>

						{/* Canvas: light grid background, consistent spacing */}
						<Box sx={{
							backgroundColor: SAP_COLORS.sapGray1 || '#f5f7fa',
							backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.06) 1px, transparent 0)',
							backgroundSize: '20px 20px',
							p: { xs: 3, md: 5 },
							minHeight: 380,
							position: 'relative',
						}}>
							{steps.length === 0 ? (
								// Empty State
								<Box sx={{ 
									display: 'flex', 
									flexDirection: 'column', 
									alignItems: 'center', 
									justifyContent: 'center',
									minHeight: 400,
									gap: 2,
									textAlign: 'center',
									px: 3
								}}>
									<Box sx={{
										width: 80,
										height: 80,
										borderRadius: '50%',
										backgroundColor: SAP_COLORS.sapGray2,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										mb: 1
									}}>
										<StepsIcon sx={{ fontSize: 40, color: SAP_COLORS.sapGray5 }} />
									</Box>
									<Typography variant="h6" sx={{ fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold, color: 'text.primary' }}>
										No Steps Yet
									</Typography>
									<Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 400 }}>
										No steps yet.{allowStepEdit ? ' Use Add Step to create the first step.' : ' You do not have permission to add steps.'}
									</Typography>
									{allowStepEdit && (
										<Button
											variant="contained"
											startIcon={<AddIcon />}
											onClick={handleAddStep}
											sx={{
												mt: 2,
												backgroundColor: SAP_COLORS.sapBlue,
												'&:hover': {
													backgroundColor: SAP_COLORS.sapBlueDark || '#1976d2'
												}
											}}
										>
											Add First Step
										</Button>
									)}
								</Box>
							) : (
								<Box
									ref={canvasRef}
									sx={{
										position: 'relative',
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										justifyContent: 'center',
										borderRadius: 2,
										p: 4,
										minHeight: 360,
										backgroundColor: 'transparent',
									}}
									role="main"
									aria-label="Step flow canvas"
									tabIndex={0}
								>
									{/* Enhanced Connector Lines */}
									<svg
										width="100%"
										height="100%"
										style={{
											position: 'absolute',
											inset: 0,
											pointerEvents: 'none',
											zIndex: 0,
										}}
										aria-hidden="true"
									>
										<defs>
											{/* Arrowhead pointing in +x in marker space; orient="auto" rotates it to match line direction so it points at next step */}
											<marker
												id="step-arrow"
												markerWidth="14"
												markerHeight="14"
												refX="12"
												refY="7"
												orient="auto"
												markerUnits="userSpaceOnUse"
											>
												<path d="M 0 2 L 12 7 L 0 12 Z" fill={SAP_COLORS.sapBlue} />
											</marker>
										</defs>
										{connectorLines.map((l, idx) => (
											<line
												key={idx}
												x1={l.x1}
												y1={l.y1}
												x2={l.x2}
												y2={l.y2}
												stroke={SAP_COLORS.sapBlue}
												strokeWidth={2}
												strokeLinecap="round"
												opacity={0.85}
												markerEnd={l.markerEnd ? 'url(#step-arrow)' : undefined}
											/>
										))}
									</svg>
									
									{/* Responsive step flow: centered, wrap in sequence (1→2→3, 4→5→6); last row right-aligned under previous */}
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'stretch',
											justifyContent: 'center',
											width: 'fit-content',
											maxWidth: '100%',
											gap: { xs: 2, sm: 3 },
											position: 'relative',
											zIndex: 1,
											py: 1,
											px: 0.5,
											overflowX: 'auto',
											overflowY: 'auto',
											scrollbarGutter: 'stable',
											WebkitOverflowScrolling: 'touch',
											'&::-webkit-scrollbar': { width: 8, height: 8 },
											'&::-webkit-scrollbar-thumb': { borderRadius: 4, bgcolor: 'action.hover' },
										}}
										role="list"
										aria-label="Step sequence"
									>
										{stepRows.map((row, rowIndex) => (
											<Box
												key={row.map((s) => s.id).join('-')}
												sx={{
													display: 'flex',
													flexDirection: 'row',
													flexWrap: 'nowrap',
													alignItems: 'center',
													// Last row: align to end so final step sits under previous row's last (1-2-3-4 then 5 under 4)
													justifyContent: rowIndex === stepRows.length - 1 ? 'flex-end' : 'center',
													gap: { xs: 3, sm: 4, md: 6 },
												}}
											>
												{row.map((step) => (
													<StepNode
														key={step.id}
														step={step}
														isSelected={selectedStepId === step.id}
														onClick={() => { setSelectedStepId(step.id); setViewMode('detail'); }}
													/>
												))}
											</Box>
										))}
									</Box>
								</Box>
							)}
						</Box>
					</CardContent>
				</Card>
			</Box>
		);
	}

	const handleBackToFlow = () => {
		if (isDirty) {
			if (window.confirm('You have unsaved changes. Discard and return to flow?')) {
				revertChanges();
				setViewMode('flow');
			}
		} else {
			setViewMode('flow');
		}
	};

	return (
		<Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
			<BackToLink label="Back to flow" onClick={handleBackToFlow} ariaLabel="Back to step flow" />
			{/* Unified tabs: Step detail, settings, request validator */}
            <StepDetailTabs
				selectedStep={selectedStep}
				editValues={editValues}
				adaptersList={adaptersList as any}
				typeOptions={typeOptions as any}
				objectTypeOptions={objectTypeOptions as any}
				resultOptions={resultOptions as any}
				responseTypeOptions={responseTypeOptions as any}
				jobDetails={jobDetails}
				steps={steps.map(s => ({ id: s.id, stepCode: s.stepCode, sequence: s.sequence }))}
				updateEditField={updateEditField as any}
				isDirty={isDirty}
				validationErrors={validationErrors}
				revertChanges={revertChanges}
				saveSelectedStep={saveSelectedStep}
				firstFieldRef={firstFieldRef}
				readOnly={!allowStepDetailEdit}
			/>
            {lastSavedAt && (
                <Box aria-live="polite" sx={{ mt: 1, color: 'text.secondary', fontSize: 12 }}>
                    Last saved at {lastSavedAt}
                </Box>
            )}
		</Box>
	);
};

export default StepDetails;